import type { MeResponse, StaffRole } from '../types/auth';

/**
 * Role lists mirrored from `apps/api/src/auth/capabilities.ts` and
 * `OPS_ACTION_OWN_ROLES`. The SPA cannot import the API map. A change to who holds
 * one of these capabilities has to change this list in the same change.
 */
const OPS_ACTION_OWN_ROLES: readonly StaffRole[] = [
  'OPS_SITE_LEADER',
  'OPS_SITE_CONTRIBUTOR',
  'OPS_ACTION_OWNER',
  'OPS_DIRECTOR',
  'COO',
  'CEO',
];

const TRIP_OVERSIGHT_ROLES: readonly StaffRole[] = ['TRIP_EVC', 'PRINCIPAL', 'OPS_DIRECTOR'];

const ATR_FUNCTION_HEAD_ROLES: readonly StaffRole[] = [
  'BUSINESS_DEVELOPMENT_DIRECTOR',
  'ESTATES_DIRECTOR',
  'FINANCE_DIRECTOR',
  'IT_DIRECTOR',
  'PEOPLE_DIRECTOR',
  'QUALITY_DIRECTOR',
];

export interface MyWorkCallerFlags {
  readonly myWorkEnabled?: boolean | undefined;
  readonly opsIssuesEnabled?: boolean | undefined;
  readonly tripsEnabled?: boolean | undefined;
  readonly atrEnabled?: boolean | undefined;
  readonly atrFunctionHeadEnabled?: boolean | undefined;
}

export interface MyWorkCaller {
  readonly roles: readonly { readonly role: StaffRole }[];
  readonly flags: MyWorkCallerFlags;
}

function holds(caller: MyWorkCaller, roles: readonly StaffRole[]): boolean {
  return caller.roles.some((assignment) => roles.includes(assignment.role));
}

/** One source applies. The shell link is the OR of these, and only while the flag is on. */
export function myWorkSourceApplies(
  caller: MyWorkCaller,
  source: 'ops_action' | 'trip_review' | 'atr_gate',
): boolean {
  if (source === 'ops_action') {
    return caller.flags.opsIssuesEnabled === true && holds(caller, OPS_ACTION_OWN_ROLES);
  }
  if (source === 'trip_review') {
    return caller.flags.tripsEnabled === true && holds(caller, TRIP_OVERSIGHT_ROLES);
  }
  if (caller.flags.atrEnabled !== true) return false;
  if (holds(caller, ['OPS_DIRECTOR'])) return true;
  if (holds(caller, ['FINANCE_APPROVER'])) return true;
  return caller.flags.atrFunctionHeadEnabled === true && holds(caller, ATR_FUNCTION_HEAD_ROLES);
}

/**
 * Chrome and `/my-work` predicate. Flag on is required. At least one source's own
 * module flag and capability must also hold. Flag off is never this predicate,
 * so My actions stays as it is today.
 */
export function myWorkApplies(caller: MyWorkCaller): boolean {
  if (caller.flags.myWorkEnabled !== true) return false;
  return (
    myWorkSourceApplies(caller, 'ops_action') ||
    myWorkSourceApplies(caller, 'trip_review') ||
    myWorkSourceApplies(caller, 'atr_gate')
  );
}

export function myWorkAppliesToMe(user: Pick<MeResponse, 'roles' | 'featureFlags'>): boolean {
  return myWorkApplies({
    roles: user.roles,
    flags: {
      myWorkEnabled: user.featureFlags.myWorkEnabled,
      opsIssuesEnabled: user.featureFlags.opsIssuesEnabled,
      tripsEnabled: user.featureFlags.tripsEnabled,
      atrEnabled: user.featureFlags.atrEnabled,
      atrFunctionHeadEnabled: user.featureFlags.atrFunctionHeadEnabled,
    },
  });
}
